import { HttpClient, HttpDownloadProgressEvent, HttpEventType, HttpHeaders, HttpUploadProgressEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, last, map, tap } from 'rxjs/operators';

import { PagedData } from '@core/models/datables';
import { getFileNameFromPath } from '@core/utils';

import { Content, FileContent } from '@repositories/models';

import { environment } from 'environments/environment';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class RepositoryFolderService  {
  rows: any;
  onKBCategoryChanged: BehaviorSubject<any>;

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   */
  constructor(private _http: HttpClient, private _router: Router, private _sanitizer: DomSanitizer) {
    // Set the defaults
    // this.onKBCategoryChanged = new BehaviorSubject({});
  }

  /**
   * Get all files
   */
   getContent(content: Content, page: number, quantity: number, sort: string) {
    return this._http.post<PagedData<FileContent>>(`${environment.repositoriesApiUrl}/api/files/page/${page}/${quantity}/${sort}`, content).pipe(
      map((response: any) => response as PagedData<FileContent>)
    );
  }

  /**
   * Obtiene las carpetas y archivos de una carpeta (carpetas primero), sin el total.
   * El total se pide aparte con countContent, así la página no espera al conteo.
   */
  getContentV2(content: Content, page: number, quantity: number, sort: string): Observable<PagedData<FileContent>> {
    return this._http.post<PagedData<FileContent>>(`${environment.repositoriesApiUrl}/api/v2/files/page/${page}/${quantity}/${sort}`, content).pipe(
      map((response: any) => response as PagedData<FileContent>),
      catchError(e => throwError(e))
    );
  }

  /**
   * Cuenta las carpetas y archivos de una carpeta
   */
  countContent(content: Content): Observable<number> {
    return this._http.post<any>(`${environment.repositoriesApiUrl}/api/v2/files/count`, content).pipe(
      map((response: any) => response.count as number),
      catchError(e => throwError(e))
    );
  }

  /**
   * Obtiene el archivo o carpeta por su id
   * @param {string} id Indica el id del archivo o carpeta
   * @returns Devuelve el archivo o carpeta
   */
  getFile(id: string): Observable<FileContent> {
    return this._http.get<FileContent>(`${environment.repositoriesApiUrl}/api/files/${id}`);
  }


  /**
   * Actualiza los usuarios que pueden visualizar un archivo
   */
  updateFileViewers(id: string, viewers: any[]): Observable<any> {
    return this._http.put<any>(
        `${environment.repositoriesApiUrl}/api/files/viewers/${id}`,
        viewers
    );
  }
  updateFileEditors(id: string, editors: any[]): Observable<any> {
    return this._http.put<any>(
        `${environment.repositoriesApiUrl}/api/files/editors/${id}`,
        editors
    );
  }

  getSharedWithMe(page: number, quantity: number, sort: string, query: string = ''): Observable<PagedData<FileContent>> {
    return this._http.get<PagedData<FileContent>>(`${environment.repositoriesApiUrl}/api/files/shared-with-me?page=${page}&quantity=${quantity}&sort=${sort}&query=${encodeURIComponent(query || '')}`);
  }

  checkExistingFile(file: FileContent): Observable<FileContent | null> {

    return this._http.post<FileContent>(
        `${environment.repositoriesApiUrl}/api/files/check-existing`,
        file
    );
  }

   /**
   * Obtiene el archivo o carpeta por el nombre del archivo
   * @param {string} fileName Indica el nombre del archivo
   * @returns Devuelve el archivo o carpeta
   */
   getFileByFileName(fileName: string): Observable<FileContent> {
    return this._http.get<FileContent>(`${environment.repositoriesApiUrl}/api/files/file-name/${fileName}`);
  }

  getUploadUrl(fileName: string) {
    return this._http.get<any>(`${environment.repositoriesApiUrl}/api/files/upload/${fileName}`);
  }

  getDownloadUrl(fileName: string) {
    return this._http.get<any>(`${environment.repositoriesApiUrl}/api/files/download/${fileName}`);
  }

  downloadFile(downloadLink: string) : Observable<Blob>{
    return this._http.get(downloadLink,{ responseType: 'blob'});
  }

  getUploadUrlThumb(fileName: string) {
    return this._http.get<any>(`${environment.repositoriesApiUrl}/api/repos/upload/${fileName}`);
  }

  uploadFile(url: string, contentType, file){
    let headers = new HttpHeaders().set('Content-Type', contentType).set('x-ms-blob-type','BlockBlob');
    return this._http.put<any>(url,file,{ headers: headers, observe: 'events', reportProgress: true});
  }

  /**
   * Función que sirve para subir un archivo a la belivpedia
   * @param {File} file Indica el archivo a subir
   * @returns {Observable<any>} Devuelve un observable con el progreso de la subida
   */
  uploadBlobFile(file: File, existingFileId?: string
  ): Observable<any> {

    const formData = new FormData();

    formData.append(
        'file',
        file,
        file.name
    );

    // Si tiene ID significa que estamos reemplazando
    // un archivo existente.
    if (existingFileId) {

      return this._http.put(
          `${environment.repositoriesApiUrl}/api/files/${existingFileId}/upload-blob`,
          formData,
          {
            observe: 'events',
            reportProgress: true
          }
      ).pipe(
          catchError((error: any) => {
            console.error('Overwrite error:', error);
            return throwError(error);
          })
      );

    }

    // Archivo nuevo
    return this._http.post(
        `${environment.repositoriesApiUrl}/api/files/upload-blob`,
        formData,
        {
          observe: 'events',
          reportProgress: true
        }
    ).pipe(
        catchError((error: any) => {
          console.error('Upload error:', error);
          return throwError(error);
        })
    );
  }

  addFileContent(file: FileContent){
    return this._http.post<FileContent>(`${environment.repositoriesApiUrl}/api/files`, file).pipe(
      map((response: any) => response.file as FileContent),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  editFileContent(file: FileContent){
    return this._http.put<FileContent>(`${environment.repositoriesApiUrl}/api/files/${file.id}`, file).pipe(
      map((response: any) => response.file as FileContent),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  deleteFileContent(fileId: string){
    return this._http.put<any>(`${environment.repositoriesApiUrl}/api/files/delete/${fileId}`,null);
  }

  
  getFoldersByRepository(repository: string) {
    return this._http.get<FileContent[]>(`${environment.repositoriesApiUrl}/api/files/folder/${repository}`);
  }

  moveFile(file: FileContent){
    return this._http.put<FileContent>(`${environment.repositoriesApiUrl}/api/files/move/${file.id}`, file).pipe(
      map((response: any) => response.file as FileContent),
      catchError(e => {
        return throwError(e);
      })
    );
  }

  /**
   * Función para descargar un archivo de la belivpedia
   * @param {string} fileName Indica el nombre del archivo a descargar
   * @returns 
   */
  downloadFileByName(fileName: string) {
    return this._http.get(`${environment.repositoriesApiUrl}/api/files/download-blob/${fileName}`, 
      { responseType: 'blob', reportProgress: true, observe: 'events' })
      .pipe(
        map(event => {
            return event;
        }),
        catchError((error: any) => {
          console.error('Download error:', error);
          return throwError(error);
        })
      );
  }

  /**
   * Función que sirve para abrir o descargar un archivo de la belivpedia
   * @param {string} filepath Indica el path del archivo en el azure storage
   */
  showFile(filepath: string){

    // Obtiene el nombre del archivo por medio de la ruta
    const fileName: string = getFileNameFromPath(filepath);

    return this.showFileByName(fileName);
  }
  
  showFileByName(fileName: string) {
    // Obtiene el archivo del repositorio por medio de la ruta
    this.getFileByFileName(fileName).subscribe(response => {
      // Si es un archivo word, pdf, excel o powerpoint lo redirige hacia la vista
      if (response.contentType == 'PDF' || response.contentType == 'WORD'
      || response.contentType == 'EXCEL' || response.contentType == 'POWERPOINT'){
        this._router.navigate(['/apps/repositories/view/'+response.id]);
      } else if (response.contentType == "URL"){ // Si es una url abre la url en una nueva pestaña
          window.open(response.url, "_blank");
      } else {
        // Si es cualquier otro archivo lo descarga y lo retorna hacia una nueva pestaña
        this.getDownloadUrl(fileName).subscribe(response => {
            window.open(response.Url, "_blank");
        });
      }
    }); 
  }

  /**
   * Función que sirve para abrir o descargar un archivo de la belivpedia
   * @param {string} fileName Indica el nombre del archivo a descargar
   * @param {string} url Indica la url del archivo a descargar
   * @returns Devuelve un objeto con el tipo de archivo y la url del archivo
   */
  getUrlForViewer(fileName: string, url: string) {
    let fileType: string = "",
      fileUrl: string | SafeResourceUrl = url;
    switch (fileName.split(".").pop()) {
      case "pdf":
        fileType = "pdf";
        break;
      case "jpg":
      case "jpeg":
      case "bmp":
      case "png":
        fileType = "img";
        fileUrl = this._sanitizer.bypassSecurityTrustResourceUrl(url)
        break;
      case "mp4":
        fileType = "video";
        break;
      default:
        let previewLink = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
          url
        )}`;
        fileUrl =
          this._sanitizer.bypassSecurityTrustResourceUrl(previewLink);
        fileType = "document";
    }
    return {
      fileType: fileType,
      url: fileUrl
    }
  }

}
